import { z } from 'zod';
import { uuidSchema } from './common.schema';

// ── Anchor type enum ─────────────────────────────────────────────────────────
export const AnchorTypeSchema = z.enum([
  'patient_mrn',
  'mother_donor_mrn',
  'sec_code',
  'feed_split',
  'batch_bottle_id',
  'receivable_id',
]);
export type AnchorType = z.infer<typeof AnchorTypeSchema>;

// ── Request schemas ──────────────────────────────────────────────────────────
export const TraceabilityListQuerySchema = z.object({
  anchor_type: AnchorTypeSchema,
  anchor_id: z.string().min(1),
  hospital_id: uuidSchema,
  /** ISO 8601 datetime — inclusive lower bound on feed_preparations.started_at */
  date_from: z.string().datetime({ offset: true }).optional(),
  /** ISO 8601 datetime — inclusive upper bound on feed_preparations.started_at */
  date_to: z.string().datetime({ offset: true }).optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().min(1).max(100).default(100),
});
export type TraceabilityListQuery = z.infer<typeof TraceabilityListQuerySchema>;

export const JourneyTypeSchema = z.enum(['split', 'batch_bottle', 'receivable', 'quick_feed']);
export type JourneyType = z.infer<typeof JourneyTypeSchema>;

export const JourneyParamsSchema = z.object({
  id: uuidSchema,
});
export type JourneyParams = z.infer<typeof JourneyParamsSchema>;

export const JourneyQuerySchema = z.object({
  /** Determines how `id` is interpreted: split UUID, milk inventory UUID, receivable UUID, or QUICK feed_preparations UUID (no split — see QuickFeedRepo.create()) */
  type: JourneyTypeSchema.default('split'),
});
export type JourneyQuery = z.infer<typeof JourneyQuerySchema>;

// ── Response item schemas ────────────────────────────────────────────────────
export const AnchorInfoSchema = z.object({
  type: AnchorTypeSchema,
  id: z.string(),
  label: z.string().optional(),
});
export type AnchorInfo = z.infer<typeof AnchorInfoSchema>;

/** One bottle/container contributing to a prep's batch group, in a search-response record. */
export const TraceabilityBottleSummarySchema = z.object({
  inventory_id: z.string(),
  barcode: z.string(),
  role: z.enum(['PRIMARY', 'SUPPLEMENT']),
  status: z.string(),
  initial_volume_ml: z.number().nullable(),
  remaining_volume_ml: z.number().nullable(),
  /** When this specific bottle (milk_inventory row) was created — used as the Batch Bottle ID search's date filter/column. */
  created_at: z.string().nullable(),
});
export type TraceabilityBottleSummary = z.infer<typeof TraceabilityBottleSummarySchema>;

/** One milk batch (and its bottles) used in a prep, in a search-response record. */
export const TraceabilityBatchGroupSchema = z.object({
  batch_id: z.string().nullable(),
  batch_number: z.string().nullable(),
  pool_number: z.string().nullable(),
  status: z.string().nullable(),
  expiry_at: z.string().nullable(),
  location_name: z.string().nullable(),
  bottles: z.array(TraceabilityBottleSummarySchema),
});
export type TraceabilityBatchGroup = z.infer<typeof TraceabilityBatchGroupSchema>;

/** One QUICK feed_preparations row that consumed a receivable's milk directly (no feed_prep_splits — see QuickFeedRepo.create()). */
export const QuickFeedUsageSchema = z.object({
  prep_id: z.string(),
  patient_mrn: z.string().nullable(),
  /** The actual administration time (feed_deliveries.delivered_at) — NOT feed_preparations.started_at,
   * which QuickFeedRepo.create() may backdate to the milk's pumped_date rather than when it was fed.
   * `null` when the preparation has not been administered yet (no feed_deliveries row). */
  fed_at: z.string().nullable(),
});
export type QuickFeedUsage = z.infer<typeof QuickFeedUsageSchema>;

/** One receivable linked to a prep's containers, in a search-response record. */
export const TraceabilityReceivableGroupSchema = z.object({
  receivable_id: z.string(),
  receipt_number: z.string(),
  collected_at: z.string().nullable(),
  /** Hospital intake timestamp — when the milk was actually received/logged. Used as the Received Bottle ID / Mother-Donor MRN search's date filter/column. */
  received_at: z.string().nullable(),
  milk_type: z.string().nullable(),
  source_type: z.string().nullable(),
  status: z.string(),
  volume_ml: z.number().nullable(),
  expiry_at: z.string().nullable(),
  location_name: z.string().nullable(),
  /** QUICK feeds that consumed this receivable's milk directly, bypassing feed_prep_splits — only populated for `receivable_id`/`mother_donor_mrn` anchor searches. */
  quick_feed_usages: z.array(QuickFeedUsageSchema).optional(),
});
export type TraceabilityReceivableGroup = z.infer<typeof TraceabilityReceivableGroupSchema>;

/** One feed split produced by a prep, in a search-response record. QUICK feeds never create a
 * feed_prep_splits row (see QuickFeedRepo.create()), so `split_id`/`split_sequence` are `null`
 * and `is_quick_feed`/`prep_id` are set instead for those synthetic entries. */
export const TraceabilityFeedGroupSchema = z.object({
  split_id: z.string().nullable(),
  split_sequence: z.number().int().nullable(),
  split_status: z.string(),
  /** Syringe / split bottle barcode printed on the label */
  split_container_barcode: z.string().nullable(),
  volume_ml: z.number().nullable(),
  location_name: z.string().nullable(),
  order_id: z.string().nullable(),
  label_expiry_at: z.string().nullable(),
  delivery_status: z.string().nullable(),
  sec_canonical_id: z.string().nullable(),
  sec_human_readable: z.string().nullable(),
  /** Set for entries synthesized from a QUICK feed_preparations row rather than a real split. */
  is_quick_feed: z.boolean().optional(),
  /** The feed_preparations.prep_id this entry came from — the stable key to use when `split_id` is null. */
  prep_id: z.string().optional(),
});
export type TraceabilityFeedGroup = z.infer<typeof TraceabilityFeedGroupSchema>;

export const SplitSummarySchema = z.object({
  prep_id: z.string(),
  prep_started_at: z.string(),
  prep_status: z.string(),
  patient_id: z.string().nullable(),
  patient_mrn: z.string().nullable(),
  /** All milk batches used in this prep, each with their constituent bottles */
  batches: z.array(TraceabilityBatchGroupSchema),
  /** Deduplicated receivables linked to all containers used in this prep */
  receivables: z.array(TraceabilityReceivableGroupSchema),
  /** Feed split details for this preparation */
  feeds: z.array(TraceabilityFeedGroupSchema),
});
export type SplitSummary = z.infer<typeof SplitSummarySchema>;

export const TraceabilityListDataSchema = z.object({
  anchor: AnchorInfoSchema,
  pagination: z.object({
    total: z.number().int(),
    page: z.number().int(),
    limit: z.number().int(),
    total_pages: z.number().int(),
  }),
  records: z.array(SplitSummarySchema),
});
export type TraceabilityListData = z.infer<typeof TraceabilityListDataSchema>;

// ── Friendly aliases for POST /api/v1/traceability/search ───────────────────
// Consumed by apps/tms-app-soho's Traceability Report UI (modules/reports).
// Structurally identical to AnchorType/AnchorInfo/SplitSummary/TraceabilityListData
// above — kept as separate names since the frontend's domain vocabulary
// ("search" results grouped by anchor) reads more naturally than the
// backend-oriented "split list" naming used elsewhere in this file.
export type TraceabilityAnchorKey = AnchorType;
export type TraceabilityAnchorInfo = AnchorInfo;
export type TraceabilitySearchRecord = SplitSummary;
export type TraceabilitySearchData = TraceabilityListData;

// ── Journey sub-schemas ──────────────────────────────────────────────────────
export const DonorSummarySchema = z.object({
  person_id: z.string(),
  donor_code: z.string().nullable(),
  person_type: z.string(),
  pseudonym: z.string().nullable(),
  is_erased: z.boolean(),
  /** Hospital-assigned MRN for the mother/donor, when available (GDPR-safe alternative to a legal name). */
  mrn: z.string().nullable(),
});
export type DonorSummary = z.infer<typeof DonorSummarySchema>;

export const ReceivableSummarySchema = z.object({
  receivable_id: z.string(),
  receipt_number: z.string(),
  volume_ml: z.number(),
  /** Pumped/collected timestamp (from the mother/donor) — distinct from `received_at` below. */
  collected_at: z.string(),
  /** Hospital intake timestamp — when the milk was actually received/logged. */
  received_at: z.string().nullable(),
  milk_type: z.string().nullable(),
  use_type: z.string(),
  /** Origin of the milk, e.g. "DONOR"/"MOM" — see milk_receivables.source_type. */
  source_type: z.string().nullable(),
  expiry_at: z.string().nullable(),
  status: z.string(),
  /** Current storage location, when available. */
  location_name: z.string().nullable(),
});
export type ReceivableSummary = z.infer<typeof ReceivableSummarySchema>;

export const PasteurizationRecordSchema = z.object({
  process_type: z.string(),
  started_at: z.string(),
  completed_at: z.string().nullable(),
  result: z.string().nullable(),
  /** Pasteurizer equipment used, from milk_pools.pasteurizer_equipment. */
  pasteurizer_equipment: z.string().nullable(),
  /** Pasteurization start time, from milk_pools.start_time. */
  start_time: z.string().nullable(),
  /** Computed as start_time + hold_time (seconds), when both are available. */
  end_time: z.string().nullable(),
  /** Pasteurization temperature (°C), from milk_pools.temperature. */
  temperature: z.number().nullable(),
});

export const PoolSummarySchema = z.object({
  pool_id: z.string(),
  pool_number: z.string(),
  source_type: z.string(),
  total_volume_ml: z.number().nullable(),
  pool_status: z.string(),
  pasteurization: z.array(PasteurizationRecordSchema),
});
export type PoolSummary = z.infer<typeof PoolSummarySchema>;

export const BatchSummarySchema = z.object({
  batch_id: z.string(),
  batch_number: z.string(),
  volume_ml: z.number().nullable(),
  status: z.string(),
  expiry_at: z.string().nullable(),
  location_name: z.string().nullable(),
});
export type BatchSummary = z.infer<typeof BatchSummarySchema>;

/**
 * Volume ledger for a single bottle/container — aggregated from
 * `milk_allocations` (fed), `feed_prep_items` (split into a preparation) and
 * `milk_discard_log` (disposed). `starting_volume_ml`/`remaining_volume_ml`
 * mirror the container's own tracked fields; the other three are `null` when
 * no matching ledger rows exist yet (not necessarily zero).
 */
export const VolumeLedgerSchema = z.object({
  starting_volume_ml: z.number().nullable(),
  remaining_volume_ml: z.number().nullable(),
  fed_volume_ml: z.number().nullable(),
  split_volume_ml: z.number().nullable(),
  disposed_volume_ml: z.number().nullable(),
});
export type VolumeLedger = z.infer<typeof VolumeLedgerSchema>;

export const ContainerSummarySchema = z.object({
  inventory_id: z.string(),
  container_barcode: z.string(),
  initial_volume_ml: z.number().nullable(),
  remaining_volume_ml: z.number().nullable(),
  status: z.string(),
  is_quality_sample: z.boolean(),
  role: z.enum(['PRIMARY', 'SUPPLEMENT']),
  /** Current storage location, when available. */
  location_name: z.string().nullable(),
  /** The receivable this container originated from — only set for MOM (mother's-own-milk) bottles that skip pooling; `null` for pooled/batched (donor) bottles. */
  receivable_id: z.string().nullable(),
  /** Aggregated volume breakdown for this container, when computable (see VolumeLedgerSchema). */
  volume_ledger: VolumeLedgerSchema.nullable(),
});
export type ContainerSummary = z.infer<typeof ContainerSummarySchema>;

export const FeedOrderSummarySchema = z.object({
  order_id: z.string(),
  order_identifier: z.string().nullable(),
  order_type: z.string(),
  order_base: z.string().nullable(),
  route: z.string().nullable(),
  /** Prescribed caloric density (e.g. kcal/mL), as ordered — the source of truth for the "Caloric Density" header field, in preference to deriving it from the preparation's actual values. */
  calorific_density: z.number().nullable(),
  /** Unit for `calorific_density`, e.g. "kcal/ml". */
  caloric_unit: z.string().nullable(),
});
export type FeedOrderSummary = z.infer<typeof FeedOrderSummarySchema>;

export const QuickFeedSummarySchema = z.object({
  quick_feed_id: z.string(),
  requested_volume_ml: z.number().nullable(),
  clinical_reason: z.string().nullable(),
  route: z.string().nullable(),
});
export type QuickFeedSummary = z.infer<typeof QuickFeedSummarySchema>;

export const PreparationDetailSchema = z.object({
  prep_id: z.string(),
  container_identifier: z.string().nullable(),
  prep_type: z.string(),
  actual_volume_ml: z.number().nullable(),
  actual_calories_kcal: z.number().nullable(),
  allergy_check_passed: z.boolean().nullable(),
  recall_check_passed: z.boolean().nullable(),
  status: z.string(),
  started_at: z.string(),
  completed_at: z.string().nullable(),
});
export type PreparationDetail = z.infer<typeof PreparationDetailSchema>;

export const ProductSummarySchema = z.object({
  product_id: z.string(),
  product_name: z.string(),
  /** Hospital-facing local/display name override, if the catalog has one. */
  display_name: z.string().nullable().optional(),
  brand_name: z.string().nullable().optional(),
  /** Form factor, e.g. "Powder", "Liquid Concentrate". */
  form_name: z.string().nullable().optional(),
  sku: z.string().nullable().optional(),
  batch_number: z.string(),
  quantity_used: z.number().nullable(),
  quantity_unit: z.string().nullable(),
  scanned_barcode: z.string().nullable(),
  expiry_date: z.string().nullable(),
});
export type ProductSummary = z.infer<typeof ProductSummarySchema>;

export const SplitDetailSchema = z.object({
  split_id: z.string(),
  split_sequence: z.number().int(),
  volume_ml: z.number(),
  status: z.string(),
  syringe_barcode: z.string().nullable(),
  label_expiry_at: z.string().nullable(),
  source_container_identifier: z.string().nullable(),
  /** Current storage location, when available. */
  location_name: z.string().nullable(),
  /** Groups sibling splits created together during a re-dosing session; null when this split was never part of a re-dose. */
  redosing_session_id: z.string().nullable(),
});
export type SplitDetail = z.infer<typeof SplitDetailSchema>;

/** A sibling split created in the same re-dosing session as the viewed split (BR — "If Redosing: Split IDs"). */
export const RedoseSplitSummarySchema = z.object({
  split_id: z.string(),
  syringe_barcode: z.string().nullable(),
  split_sequence: z.number().int(),
});
export type RedoseSplitSummary = z.infer<typeof RedoseSplitSummarySchema>;

export const DeliverySummarySchema = z.object({
  delivery_id: z.string(),
  status: z.string(),
  delivered_at: z.string().nullable(),
  care_unit_id: z.string().nullable(),
});
export type DeliverySummary = z.infer<typeof DeliverySummarySchema>;

export const PatientSummarySchema = z.object({
  patient_id: z.string(),
  mrn: z.string().nullable(),
  pseudonym: z.string().nullable(),
  is_erased: z.boolean(),
  encounter_id: z.string().nullable(),
});
export type PatientSummary = z.infer<typeof PatientSummarySchema>;

export const SecDetailSchema = z.object({
  sec_traceability_id: z.string(),
  canonical_id: z.string(),
  sec_human_readable: z.string(),
  eutc_id: z.string().nullable(),
  isbt_id: z.string().nullable(),
  te_code: z.string().nullable(),
  created_at: z.string(),
});
export type SecDetail = z.infer<typeof SecDetailSchema>;

// ── Timeline ─────────────────────────────────────────────────────────────────
export const TimelineEventSchema = z.object({
  /** ISO 8601 — primary sort key */
  occurred_at: z.string(),
  /** Past-tense human-readable description, e.g. "Milk received from donor" */
  verb: z.string(),
  /** Logical entity type: 'milk_receivable' | 'pool' | 'batch' | 'bottle' | 'preparation' | 'split' | 'sec' | 'delivery' */
  entity_type: z.string(),
  /** UUID of the entity this event relates to */
  entity_id: z.string(),
  /** Location associated with this event, when available */
  location: z.string().nullable().optional(),
  /** Source location for movement events */
  from_location: z.string().nullable().optional(),
  /** Destination location for movement events */
  to_location: z.string().nullable().optional(),
  /** Optional human-readable detail: pool_number, batch_number, donor_code, mrn … */
  detail: z.string().optional(),
  /** Display name of the staff member who performed the action (resolved from the
   * entity's created_by/performed_by/etc. column via tmens_users, falling back to
   * Cognito), or null when no actor is recorded/resolvable. */
  actor: z.string().nullable().optional(),
});
export type TimelineEvent = z.infer<typeof TimelineEventSchema>;

// ── Journey response schema ──────────────────────────────────────────────────
export const TraceabilityRecordSchema = z.object({
  anchor: AnchorInfoSchema,
  donors: z.array(DonorSummarySchema),
  receivables: z.array(ReceivableSummarySchema),
  pools: z.array(PoolSummarySchema),
  batches: z.array(BatchSummarySchema),
  containers: z.array(ContainerSummarySchema),
  feed_order: FeedOrderSummarySchema.nullable(),
  quick_feed: QuickFeedSummarySchema.nullable(),
  preparation: PreparationDetailSchema.nullable(),
  products_used: z.array(ProductSummarySchema),
  split: SplitDetailSchema.nullable(),
  delivery: DeliverySummarySchema.nullable(),
  patient: PatientSummarySchema.nullable(),
  sec: SecDetailSchema.nullable(),
  /** Sibling splits created in the same re-dosing session as the viewed split; empty when not a re-dose. */
  redose_splits: z.array(RedoseSplitSummarySchema),
  /** Chronological narrative of every state change in the milk-to-bedside chain */
  timeline: z.array(TimelineEventSchema),
});
export type TraceabilityRecord = z.infer<typeof TraceabilityRecordSchema>;
